import { useState, useEffect, useRef } from 'react'
import { requestForToken, onMessageListener, initializeMessaging } from '../firebase-config'
import useUIActions from './useUIActions'
import storageNames from 'constants/storageNames'
import NotificationsService from 'services/notifications'
import { useSelector } from 'react-redux'

const useFirebaseMessaging = () => {
	const {
		session: { userId }
	} = useSelector(state => state)
	const [fcmToken, setFcmToken] = useState(null)
	const [notificationPermission, setNotificationPermission] = useState('default')
	const messageListenerRef = useRef(null)
	const tokenSavedRef = useRef(false)
	const initAttemptedRef = useRef(false)

	const { handleCloseModal } = useUIActions()

	// Comprobar el permiso de notificaciones actual
	const checkNotificationPermission = async () => {
		if (!('Notification' in window)) {
			console.log('Este navegador no soporta notificaciones')
			return false
		}
		const permission = await Notification.permission
		setNotificationPermission(permission)
		return permission === 'granted'
	}

	// Guardar el token en el servidor
	const saveToken = async token => {
		if (!token || !userId || tokenSavedRef.current) return

		try {
			const payload = { token }
			await NotificationsService.postToken({ payload, userId })
			tokenSavedRef.current = true
		} catch (error) {
			console.error('Error al guardar el token:', error)
		}
	}

	// Inicializar Firebase Messaging
	const initMessaging = async () => {
		if (initAttemptedRef.current) return

		initAttemptedRef.current = true
		await initializeMessaging()

		// Intentar recuperar token almacenado si ya tenemos permiso
		if (Notification.permission === 'granted') {
			const storedToken = localStorage.getItem(storageNames.notification_push)
			if (storedToken) {
				setFcmToken(storedToken)
				saveToken(storedToken)

				// De todas formas solicitamos un token nuevo por si ha cambiado
				try {
					const newToken = await requestForToken()
					if (newToken && newToken !== storedToken) {
						setFcmToken(newToken)
						localStorage.setItem(storageNames.notification_push, newToken)
						saveToken(newToken)
					}
				} catch (error) {
					console.error('Error al actualizar token:', error)
				}
			} else {
				// No hay token almacenado, solicitamos uno nuevo
				try {
					const newToken = await requestForToken()
					if (newToken) {
						setFcmToken(newToken)
						localStorage.setItem(storageNames.notification_push, newToken)
						saveToken(newToken)
					}
				} catch (error) {
					console.error('Error al solicitar token:', error)
				}
			}
		}
	}

	// Solicitar permiso de notificaciones y obtener token
	const requestNotificationPermission = async () => {
		try {
			const permission = await Notification.requestPermission()
			setNotificationPermission(permission)

			if (permission === 'granted') {
				// Solicitamos un nuevo token
				const token = await requestForToken()

				if (token) {
					setFcmToken(token)
					localStorage.setItem(storageNames.notification_push, token)
					saveToken(token)
				} else {
					console.warn('No se pudo obtener el token FCM')
				}

				handleCloseModal()
			} else {
				console.log('Permiso de notificación:', permission)
			}
		} catch (error) {
			console.error('Error al solicitar permiso de notificación:', error)
		}
	}

	// Inicializar al montar el componente
	useEffect(() => {
		const initialize = async () => {
			await checkNotificationPermission()
			await initMessaging()
		}

		initialize()

		// Limpieza al desmontar
		return () => {
			if (messageListenerRef.current) {
				messageListenerRef.current()
				messageListenerRef.current = null
			}
		}
	}, [])

	// Configurar listener de mensajes cuando tenemos permiso
	useEffect(() => {
		if (notificationPermission !== 'granted') return

		// Evitar configurar múltiples listeners
		if (messageListenerRef.current) return

		// Configurar el listener de mensajes
		const setupMessageListener = async () => {
			try {
				const unsubscribe = onMessageListener()
				messageListenerRef.current = unsubscribe
			} catch (error) {
				console.error('Error al configurar el listener de mensajes:', error)
			}
		}

		setupMessageListener()
	}, [notificationPermission])

	return { fcmToken, notificationPermission, requestNotificationPermission }
}

export default useFirebaseMessaging
